/**
 * 금액 시드 — `project_contract`(+개정 이력·단계별 용역비·기성 차수·수금), `project_outsourcing`.
 *
 * 계약 금액은 계약 행에 저장하지 않는다. 개정 이력(`revision`)의 기간에서 그 시점 값을 뽑는다 —
 * 증액을 해도 지난 수익성 계산이 그대로 재현되어야 한다. 기성 차수에는 상태 칸이 없다:
 * `billed_on` 이 비면 예정이고, 수금됐는지는 붙은 수금의 합으로 본다.
 *
 * 외주는 종류에 따라 수익성에서 다르게 쓰인다 — 외주는 우리가 떠안는 비용, 추가 용역은 발주처에
 * 되청구할 수 있는 것, 진행비는 발주 없이 나가는 것이다.
 */
import type { Billing, Contract, ContractRevision, Income, Outsourcing } from "@/types";
import { CLIENT_NAMES, phaseFeesOf, phaseId, projects } from "./projects";

export interface MockContract extends Omit<Contract, "amount" | "billings"> {
  project_id: string;
}

export interface MockBilling extends Omit<Billing, "received" | "receivable" | "incomes"> {
  project_id: string;
  contract_id: string;
}

export interface MockIncome extends Income {
  project_id: string;
  contract_id: string;
}

export interface MockOutsourcing extends Outsourcing {
  project_id: string;
}

const contractId = (projectId: string) => `contract-${projectId.slice(2)}`;

/** 계약을 넣은 프로젝트와 계약일, 기간. 계약 전(착수예정)은 계약이 없다. */
const CONTRACT_SEED: Record<string, { signed_on: string; from: string; to: string }> = {
  "p-2508": { signed_on: "2025-11-03", from: "2025-11-03", to: "2026-12-31" },
  "p-2410": { signed_on: "2025-12-01", from: "2025-12-01", to: "2027-06-30" },
  "p-2601": { signed_on: "2026-01-12", from: "2026-01-12", to: "2026-12-31" },
  "p-2506": { signed_on: "2026-03-02", from: "2026-03-02", to: "2027-03-31" },
  "p-2604": { signed_on: "2026-04-06", from: "2026-04-06", to: "2027-12-31" },
  "p-2511": { signed_on: "2026-05-11", from: "2026-05-11", to: "2027-02-28" },
  "p-2404": { signed_on: "2026-06-01", from: "2026-06-01", to: "2027-01-31" },
  "p-2503": { signed_on: "2026-06-22", from: "2026-06-22", to: "2027-06-30" },
  "p-2305": { signed_on: "2023-05-22", from: "2023-05-22", to: "2025-08-31" },
  "p-2106": { signed_on: "2025-02-17", from: "2025-02-17", to: "2026-08-31" },
};

/** 계약 금액이 바뀐 곳 — 이전 금액은 무효가 아니라 기간이 닫힌다. */
const RAISE: Record<string, { on: string; add: number; reason: string }> = {
  // 홍보관 계획이 더해져 설계변경 2차로 증액했다.
  "p-2503": { on: "2026-10-05", add: 38_000_000, reason: "설계변경 2차(홍보관 계획) 반영 증액" },
};

export const contracts: MockContract[] = projects
  .filter((project) => CONTRACT_SEED[project.id])
  .map((project) => {
    const seed = CONTRACT_SEED[project.id];
    const fees = phaseFeesOf(project.id);
    const base = fees.reduce((sum, fee) => sum + fee.fee, 0);
    const raise = RAISE[project.id];
    const revisions: ContractRevision[] = [
      {
        id: `revision-${project.id}-1`,
        amount: raise ? base - raise.add : base,
        effective_from: seed.signed_on,
        effective_to: raise?.on ?? null,
        change_reason: "",
        voided_at: null,
        void_reason: "",
        created_at: `${seed.signed_on}T10:00:00+09:00`,
      },
    ];
    if (raise)
      revisions.push({
        id: `revision-${project.id}-2`,
        amount: base,
        effective_from: raise.on,
        effective_to: null,
        change_reason: raise.reason,
        voided_at: null,
        void_reason: "",
        created_at: `${raise.on}T14:00:00+09:00`,
      });
    return {
      id: contractId(project.id),
      project_id: project.id,
      seq: 1,
      title: `${project.name} 설계용역`,
      kind: "design",
      kind_label: "설계비",
      client_name: CLIENT_NAMES[project.id] ?? "",
      signed_on: seed.signed_on,
      contract_from: seed.from,
      contract_to: seed.to,
      vat_included: false,
      memo: "",
      revisions,
      phase_fees: fees,
    };
  });

/** 기성 차수 — 단계에 맞춰 끊는다. 아직 청구하지 않은 차수는 billed_on 이 비어 있다. */
const BILLING_SEED: {
  project: string;
  seq: number;
  title: string;
  amount: number;
  phase?: string;
  planned_on?: string;
  billed_on?: string;
  due_on?: string;
}[] = [
  {
    project: "p-2508",
    seq: 1,
    title: "착수금",
    amount: 64_000_000,
    phase: "기획설계",
    billed_on: "2025-11-10",
    due_on: "2025-11-30",
  },
  {
    project: "p-2508",
    seq: 2,
    title: "기본설계 완료",
    amount: 128_000_000,
    phase: "기본설계",
    billed_on: "2026-09-15",
    due_on: "2026-09-30",
  },
  {
    project: "p-2508",
    seq: 3,
    title: "실시설계 완료",
    amount: 128_000_000,
    phase: "실시설계",
    planned_on: "2026-11-05",
  },
  {
    project: "p-2410",
    seq: 1,
    title: "착수금",
    amount: 65_000_000,
    phase: "기획설계",
    billed_on: "2025-12-05",
    due_on: "2025-12-31",
  },
  {
    project: "p-2410",
    seq: 2,
    title: "기본설계 완료",
    amount: 97_500_000,
    phase: "기본설계",
    billed_on: "2026-08-25",
    due_on: "2026-09-25",
  },
  {
    project: "p-2410",
    seq: 3,
    title: "허가 완료",
    amount: 81_000_000,
    phase: "허가접수",
    planned_on: "2026-11-10",
  },
  {
    project: "p-2601",
    seq: 1,
    title: "착수금",
    amount: 21_000_000,
    phase: "기획설계",
    billed_on: "2026-01-20",
    due_on: "2026-02-10",
  },
  {
    project: "p-2506",
    seq: 1,
    title: "착수금",
    amount: 10_500_000,
    phase: "기본설계",
    billed_on: "2026-03-10",
    due_on: "2026-03-31",
  },
  { project: "p-2506", seq: 2, title: "신고 수리", amount: 7_000_000, planned_on: "2026-11-20" },
  {
    project: "p-2604",
    seq: 1,
    title: "착수금",
    amount: 36_000_000,
    phase: "기획설계",
    billed_on: "2026-04-15",
    due_on: "2026-05-15",
  },
  {
    project: "p-2604",
    seq: 2,
    title: "기본설계 완료",
    amount: 54_000_000,
    phase: "기본설계",
    planned_on: "2026-11-05",
  },
  {
    project: "p-2511",
    seq: 1,
    title: "착수금",
    amount: 30_000_000,
    phase: "기획설계",
    billed_on: "2026-05-20",
    due_on: "2026-06-10",
  },
  {
    project: "p-2404",
    seq: 1,
    title: "착수금",
    amount: 42_000_000,
    phase: "기본설계",
    billed_on: "2026-06-10",
    due_on: "2026-06-30",
  },
  {
    project: "p-2503",
    seq: 1,
    title: "착수금",
    amount: 121_500_000,
    phase: "기획설계",
    billed_on: "2026-07-01",
    due_on: "2026-07-31",
  },
  {
    project: "p-2503",
    seq: 2,
    title: "기본설계 완료",
    amount: 121_500_000,
    phase: "기본설계",
    billed_on: "2026-10-07",
    due_on: "2026-11-06",
  },
  {
    project: "p-2305",
    seq: 1,
    title: "설계비 일시",
    amount: 286_000_000,
    billed_on: "2024-05-20",
    due_on: "2024-06-20",
  },
  {
    project: "p-2106",
    seq: 1,
    title: "설계비",
    amount: 140_000_000,
    phase: "기본설계",
    billed_on: "2025-03-05",
    due_on: "2025-03-31",
  },
  {
    project: "p-2106",
    seq: 2,
    title: "감리비",
    amount: 60_000_000,
    phase: "감리",
    billed_on: "2026-08-28",
    due_on: "2026-09-30",
  },
];

export const billings: MockBilling[] = BILLING_SEED.map((seed) => ({
  id: `billing-${seed.project}-${seed.seq}`,
  project_id: seed.project,
  contract_id: contractId(seed.project),
  seq: seed.seq,
  title: seed.title,
  amount: seed.amount,
  phase_id: seed.phase ? phaseId(seed.project, seed.phase) : null,
  planned_on: seed.planned_on ?? null,
  billed_on: seed.billed_on ?? null,
  due_on: seed.due_on ?? null,
  memo: "",
}));

/** 수금 — 차수에 붙는다. 세종로공원 2차와 이태원 주택 감리비는 아직 들어오지 않아 미수금이다. */
const UNPAID = new Set(["billing-p-2410-2", "billing-p-2106-2"]);

export const incomes: MockIncome[] = billings
  .filter((billing) => billing.billed_on && !UNPAID.has(billing.id))
  .map((billing, index) => ({
    id: `income-${index + 1}`,
    project_id: billing.project_id,
    contract_id: billing.contract_id,
    billing_id: billing.id,
    amount: billing.amount,
    received_on: billing.due_on ?? billing.billed_on!,
    memo: "",
  }));

/** 외주와 진행비. */
export const outsourcings: MockOutsourcing[] = [
  {
    id: "outsourcing-1",
    project_id: "p-2410",
    seq: 1,
    kind: "outsource",
    kind_label: "외주",
    payee_name: "한빛구조기술사사무소",
    title: "주차장 구조 설계",
    amount: 32_000_000,
    vat_included: false,
    ordered_on: "2026-03-10",
    planned_on: "2026-09-30",
    paid_on: "2026-09-28",
    phase_id: phaseId("p-2410", "기본설계"),
    memo: "",
  },
  {
    id: "outsourcing-2",
    project_id: "p-2410",
    seq: 2,
    kind: "outsource",
    kind_label: "외주",
    payee_name: "대명설비엔지니어링",
    title: "기계·전기 설비 설계",
    amount: 24_000_000,
    vat_included: false,
    ordered_on: "2026-04-01",
    planned_on: "2026-11-30",
    paid_on: null,
    phase_id: phaseId("p-2410", "허가접수"),
    memo: "허가 완료 후 지급",
  },
  {
    id: "outsourcing-3",
    project_id: "p-2506",
    seq: 1,
    kind: "outsource",
    kind_label: "외주",
    payee_name: "모형공방",
    title: "벤치·사이니지 목업 제작",
    amount: 6_000_000,
    vat_included: false,
    ordered_on: "2026-03-20",
    planned_on: "2026-08-31",
    paid_on: "2026-08-31",
    phase_id: phaseId("p-2506", "기본설계"),
    memo: "",
  },
  {
    id: "outsourcing-4",
    project_id: "p-2604",
    seq: 1,
    kind: "extra_service",
    kind_label: "추가 용역",
    payee_name: "한빛구조기술사사무소",
    title: "견본주택 가설 구조 검토",
    amount: 6_000_000,
    vat_included: false,
    ordered_on: "2026-09-10",
    planned_on: "2026-10-31",
    paid_on: null,
    phase_id: phaseId("p-2604", "기본설계"),
    memo: "발주처 되청구 예정",
  },
  {
    id: "outsourcing-5",
    project_id: "p-2508",
    seq: 1,
    kind: "direct",
    kind_label: "진행비",
    payee_name: "서울시 성동구청",
    title: "인지대·면허세",
    amount: 1_240_000,
    vat_included: true,
    ordered_on: null,
    planned_on: null,
    paid_on: "2026-09-20",
    phase_id: null,
    memo: "",
  },
  {
    id: "outsourcing-6",
    project_id: "p-2503",
    seq: 1,
    kind: "direct",
    kind_label: "진행비",
    payee_name: "모형공방",
    title: "1/200 매스 모형",
    amount: 3_600_000,
    vat_included: true,
    ordered_on: "2026-08-10",
    planned_on: null,
    paid_on: "2026-08-25",
    phase_id: null,
    memo: "",
  },
];
